/* eslint-disable no-return-await */
/* eslint-disable no-param-reassign  */

const {
  REACT_APP_CONTENTFUL_SPACE_ID: contentfulSpaceId,
  REACT_APP_CONTENTFUL_ACCESS_TOKEN: contentfulAccessToken,
  REACT_APP_CONTENTFUL_PREVIEW_ACCESS_TOKEN: contentfulPreviewAccessToken,
  REACT_APP_MODE: mode
} = process.env

const accessToken = mode === "preview" ? contentfulPreviewAccessToken : contentfulAccessToken
const isPreview = mode === "preview"

const fetchGraphql = async query => {
  const res = await fetch(`https://graphql.contentful.com/content/v1/spaces/${contentfulSpaceId}/environments/master`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`
    },
    body: JSON.stringify({ query })
  })

  return await res.json()
}

export const fetchGlobalData = async lang => {
  const res = await fetchGraphql(`
    query {
      homepageCollection(where: { language: "${lang}" }, limit: 1) {
        items {
          logo {
            url(transform: { format: WEBP, quality: 90 })
          }
          seoTitle
          seoDescription
          footerLogo {
            url(transform: { format: WEBP, quality: 90 })
          }
          footerLinks
          footerDisclaimer
          socialIcons
          registrationForm
        }
      }
      pagesCollection(where: { language: "${lang}" }) {
        items {
          slug
          pageName
        }
      }
      translationsCollection(where: { language: "${lang}" }, limit: 1) {
        items {
          translations
        }
      }
      testimonialsCollection(where: { language: "${lang}" }) {
        total
      }
      newsCollection(where: { language: "${lang}" }) {
        total
      }
    }  
  `)

  // Error handling

  const { logo, footerLogo, footerLinks, footerDisclaimer, socialIcons, seoTitle, seoDescription, registrationForm } =
    res.data.homepageCollection.items[0]
  const legalPages = res.data.pagesCollection.items
  const { translations } = res.data.translationsCollection.items[0]

  const newsTotal = res.data.newsCollection.total

  const testimonialsTotal = res.data.testimonialsCollection.total

  return {
    lang,
    logo: logo.url,
    registrationForm,
    navLinks: [
      {
        text: `${translations.about}`,
        to: `./about`,
        matchPath: `/${translations.about.toLowerCase()}`
      },
      {
        text: `${translations.faq.toUpperCase()}`,
        to: `./faq`,
        matchPath: `/${translations.faq.toLowerCase()}`
      },
      ...(newsTotal !== 0
        ? [
            {
              text: `${translations.news}`,
              to: `./news`,
              matchPath: `/${translations.news.toLowerCase()}`
            }
          ]
        : []),
      ...(testimonialsTotal !== 0
        ? [
            {
              text: `${translations.testimonials}`,
              to: `./testimonials`,
              matchPath: `/${translations.testimonials.toLowerCase()}`
            }
          ]
        : [])
    ],
    seo: {
      title: seoTitle,
      description: seoDescription
    },
    footer: {
      footerLogo,
      footerDisclaimer,
      legalLinks: legalPages.map(({ pageName, slug }) => ({ label: pageName, slug })),
      ourSitesLinks: footerLinks,
      socialLinks: socialIcons
    },
    translations
  }
}

export const fetchHomePageData = async lang => {
  const res = await fetchGraphql(`
    query {
      pageLanguages: homepageCollection {
        items {
          language
        }
      }
      homepageCollection(where: { language: "${lang}" }, limit: 1) {
        items {
          aboutTitle
          aboutText
          usps
          contentSection
          contactFormText
          bannerSectionCollection(limit: 1) {
            items {
              bannerImage {
                url
                contentType
              }
              mobileMedia {
                url
                contentType
              }
              content
            }
          }
        }
      }
      translationsCollection(where: { language: "${lang}" }, limit: 1) {
        items {
          translations
        }
      }
      newsCollection(
        where: {AND: [{language: "${lang}"}, {OR: [{hideFromNewsFeed_exists: false}, {hideFromNewsFeed: false}]}]}
        order: newsDate_DESC
        limit: 4
      ) {
        items {
          newsTitle
          newsThumbnail {
            url(transform: {format: WEBP, quality: 90, width: 284})
          }
          slug
          categoryCollection {
            items {
              categoryName
            }
          }
        }
      }
      testimonialsCollection(where: { language: "${lang}" }, limit: 1) {
        items {
          testimonialsListCollection {
            items {
              affiliateName
              text
              url
              affiliateImage {
                url(transform: { format: WEBP, quality: 90, width: 95 })
              }
            }
          }
        }
      }
    }
  `)

  // Error handling

  const pageLanguages = res.data.pageLanguages.items?.map(({ language }) => language)
  const { aboutTitle, aboutText, usps, contentSection, contactFormText } = res.data.homepageCollection.items[0]
  const news = res.data.newsCollection.items
  const testimonials = res.data.testimonialsCollection.items[0]?.testimonialsListCollection?.items
  const banner = res.data.homepageCollection.items[0].bannerSectionCollection.items[0]

  return {
    pageLanguages,
    banner: {
      mobileMedia: banner.mobileMedia,
      desktopMedia: banner.bannerImage,
      content: banner.content,
      imageSeparator: banner.imageSeparator
    },
    about: {
      title: aboutTitle,
      text: aboutText
    },
    usps,
    contentSection,
    contactFormText,
    news: news.map(({ newsTitle, newsThumbnail: { url: imageUrl }, slug, categoryCollection }) => ({
      title: newsTitle,
      imageUrl,
      slug,
      categories: categoryCollection.items.reduce((tot, cur) => [...tot, cur.categoryName], [])
    })),
    testimonials: testimonials?.map(({ affiliateName, affiliateImage: { url: logoUrl }, url, text }) => ({
      name: affiliateName,
      logoUrl,
      url,
      text
    }))
  }
}

export const fetchFaqPageData = async lang => {
  const res = await fetchGraphql(`
    query {
      pageLanguages: faqCollection {
        items {
          language
        }
      }
      faqPageCollection(where: { language: "${lang}" }, limit: 1) {
        items {
          seoTitle
          seoDescription
          faqTitle
          faqContent
        }
      }
      faqCollection(preview: ${isPreview}, where: { language: "${lang}" }, order: order_ASC) {
        items {
          question
          answer
        }
      }
    }  
  `)

  // Error handling

  const { seoTitle, seoDescription, faqTitle, faqContent } = res.data.faqPageCollection.items[0]
  const pageLanguages = res.data.pageLanguages.items?.map(({ language }) => language)
  const faqs = res.data.faqCollection.items

  return {
    pageLanguages,
    seoTitle,
    seoDescription,
    faqTitle,
    faqContent,
    faqs
  }
}

export const fetchAboutPageData = async lang => {
  const res = await fetchGraphql(`
    query {
      aboutCollection(where: { language: "${lang}" }, limit: 1) {
        items {
          brandBannerImageDesktop {
            url
          }
          brandBannerImageMobile {
            url
          }
          bannerText
          brandDescription
          contentSectionText
          brandName
          brandUrl
          seoDescription
          seoTitle
          slug
          showRegisterButton
        }
      }
      translationsCollection(where: { language: "${lang}" }, limit: 1) {
        items {
          translations
        }
      }
    }
  `)

  const {
    brandBannerImageDesktop,
    brandBannerImageMobile,
    brandDescription,
    contentSectionText,
    brandName,
    brandUrl,
    bannerText,
    seoDescription,
    seoTitle,
    slug,
    showRegisterButton
  } = res.data.aboutCollection.items[0]

  return {
    description: brandDescription,
    desktopBannerImageUrl: brandBannerImageDesktop?.url,
    mobileBannerImageUrl: brandBannerImageMobile?.url,
    name: brandName,
    bannerText,
    contentSectionText,
    seoDescription,
    seoTitle,
    slug,
    showRegisterButton,
    url: brandUrl
  }
}

export const fetchTestimonialsPageData = async lang => {
  const res = await fetchGraphql(`
    query {
      pageLanguages: testimonialsCollection {
        items {
          language
        }
      }
      testimonialsCollection(where: { language: "${lang}" }, limit: 1) {
        items {
          testimonialsListCollection {
            items {
              affiliateName
              text
              url
              affiliateImage {
                url(transform: { format: WEBP, quality: 90, width: 95 })
              }
            }
          }
        }
      }
    }
  `)

  // Error handling
  const pageLanguages = res.data.pageLanguages.items?.map(({ language }) => language)
  const testimonials = res.data.testimonialsCollection.items[0].testimonialsListCollection.items

  return {
    pageLanguages,
    testimonials: testimonials.map(({ affiliateName, text, url, affiliateImage }) => ({
      affiliateName,
      text,
      url,
      affiliateLogoUrl: affiliateImage.url
    }))
  }
}

export const fetchRegistrationPageData = async () => {
  const res = await fetchGraphql(`
    query {
      registrationCollection(where: { language: "en" }) {
        items {
          popupContent
          popupCloseButtonText
        }
      }
    }    
  `)

  // Error handling

  return {
    popup: {
      content: res.data.registrationCollection.items[0].popupContent,
      closeButtonText: res.data.registrationCollection.items[0].popupCloseButtonText
    }
  }
}

export const fetchInfoPageData = async (lang, slug) => {
  const res = await fetchGraphql(`
    query {
      pageLanguages: pagesCollection {
        items {
          language
        }
      }
      pagesCollection(where: { language: "${lang}", slug: "${slug}" }, limit: 1) {
        items {
          title
          content
          content2
        }
      }
    }  
  `)

  // Error handling

  const pageLanguages = [...new Set(res.data.pageLanguages.items?.map(({ language }) => language))]
  const { title, content, content2 } = res.data.pagesCollection.items[0]

  return {
    pageLanguages,
    slug,
    title,
    content,
    content2
  }
}

const numberOfArticlesPerPage = 8
export const fetchNewsPageData = async (searchParams, lang) => {
  const page = searchParams.get("page")
  const brand = searchParams.get("brand")
  const search = searchParams.get("search")

  /*
    I'm using newsCategoryCollection to get the available languages of the news page
    because we do not have a model for the news page itself and querying based on the
    articles themselves would be too complex as there may be too many articles. There
    will be far less categories.
  */
  const res = await fetchGraphql(`
    query {
      pageLanguages: newsCategoryCollection {
        items {
          language
        }
      }
      articles: newsCollection(where: {
        language: "${lang}"
        ${brand ? `, category: { categoryName: "${brand}" }` : ""}
        AND: [
          {
            OR: [
              {
                AND: {
                  hideFromNewsFeed_exists: true
                  hideFromNewsFeed: false
                }
              }
              { hideFromNewsFeed_exists: false }
            ]
          }
          ${
            search
              ? `{
            OR: {
              newsTitle_contains: "${search}"
              newsText_contains: "${search}"
            }
          }`
              : ""
          }
        ]
      }) {
        numberOfArticles: total
      }
      newsCategoryCollection(where: { language: "${lang}", categoryType: "brands" }) {
        items {
          categoryName
        }
      }
      newsCollection(where: {
        language: "${lang}"
        ${brand ? `, category: { categoryName: "${brand}" }` : ""}
        AND: [
          {
            OR: [
              {
                AND: {
                  hideFromNewsFeed_exists: true
                  hideFromNewsFeed: false
                }
              }
              { hideFromNewsFeed_exists: false }
            ]
          }
          ${
            search
              ? `{
            OR: {
              newsTitle_contains: "${search}"
              newsText_contains: "${search}"
            }
          }`
              : ""
          }
        ]
      } order: newsDate_DESC limit: ${numberOfArticlesPerPage} skip: ${page ? (page - 1) * 8 : 0}) {
        items {
          newsThumbnail {
            url(transform: { format: WEBP, quality: 90, width: 275 })
          }
          newsTitle
          categoryCollection {
            items {
              categoryName
            }
          }
          slug
        }
      }
    }
  `)

  const pageLanguages = [...new Set(res.data.pageLanguages.items?.map(({ language }) => language))]
  const articles = res.data.newsCollection.items
  const { numberOfArticles: numberOfTotalArticles } = res.data.articles
  const categories = res.data.newsCategoryCollection.items

  return {
    pageLanguages,
    numberOfArticlesPerPage,
    numberOfTotalArticles,
    categories: categories.map(({ categoryName }) => ({ label: categoryName, value: categoryName })),
    articles: articles.map(({ newsThumbnail: { url }, slug, newsTitle, categoryCollection }) => ({
      imageUrl: url,
      title: newsTitle,
      slug,
      categories: categoryCollection.items.reduce((tot, category) => {
        if (!category) return tot

        return tot.concat(category.categoryName)
      }, [])
    }))
  }
}

export const fetchNewsArticlePageData = async (slug, lang) => {
  const res = await fetchGraphql(`
    query {
      newsCollection(
        where: { language: "${lang}", slug: "${slug}" }
        limit: 1
      ) {
        items {
          seoTitle
          seoDescription
          newsImage {
            url(transform: { format: WEBP, quality: 90, width: 1070 })
          }
          newsDate
          categoryCollection {
            items {
              categoryName
            }
          }
          newsTitle
          newsText
          downloadableLogosCollection {
            items {
              description
              fileName
              url
            }
          }
          hasRace
          raceTitle
          raceStartDate
          raceEndDate
          raceLeaderboardTitle
          raceLeaderboard
          raceLeaderboardTwo
          raceLeaderboardTwoTitle
        }
      }
    }
  `)

  const article = res.data.newsCollection.items[0]

  const {
    seoTitle,
    seoDescription,
    newsImage,
    newsDate,
    categoryCollection,
    newsTitle,
    newsText,
    downloadableLogosCollection,
    raceTitle,
    raceStartDate,
    raceEndDate,
    raceLeaderboardTitle,
    raceLeaderboard,
    raceLeaderboardTwoTitle,
    raceLeaderboardTwo
  } = article

  return {
    lang,
    seoTitle,
    seoDescription,
    slug,
    bannerImageUrl: newsImage.url,
    date: newsDate,
    categories: categoryCollection.items.reduce((tot, category) => {
      if (!category) return tot

      return [...tot, category.categoryName]
    }, []),
    title: newsTitle,
    text: newsText,
    downloadableLogos: downloadableLogosCollection.items,
    race: {
      raceTitle,
      raceStartDate,
      raceEndDate,
      raceLeaderboardTitle,
      raceLeaderboard,
      raceLeaderboardTwoTitle,
      raceLeaderboardTwo
    }
  }
}

export const fetchRelatedNewsArticles = async (slugToIgnore, categories, lang) => {
  const _categories = categories.map(cat => `"${cat}"`).join(",")

  const res = await fetchGraphql(`
    query {
      newsCollection(
        where: { language: "${lang}", slug_not: "${slugToIgnore}", category: { categoryName_in: [${_categories}] } }
        limit: 4
      ) {
        items {
          slug
          newsTitle
          newsThumbnail {
            url(transform: {
              format: WEBP
              quality: 90
              width: 270
            })
          }
          categoryCollection {
            items {
              categoryName
            }
          }
        }
      }
    }
  `)

  const articles = res.data.newsCollection.items

  return articles.map(({ newsThumbnail: { url }, slug, newsTitle, categoryCollection }) => ({
    imageUrl: url,
    title: newsTitle,
    slug,
    categories: categoryCollection.items.reduce((tot, articleCategory) => {
      if (!articleCategory) return tot

      return [...tot, articleCategory.categoryName]
    }, [])
  }))
}
